import classNames from "classnames";
import { useCallback, useMemo, useState } from "react";

import {
  borderRadiusSizes,
  borderRadiusWithBorderSizes,
  boxSizes,
  textSizes,
} from "./constants";
import type { UserImageSizes } from "./constants";
import { nameToHslColor, nameToInitials } from "./name";

export interface UserImageProps {
  /** Class name */
  className?: string;
  /** Whether to show a border around the image */
  hasBorder?: boolean;
  /** Whether to use full rounded border-radius */
  isFullRounded?: boolean;
  /** User name */
  name: string;
  /** Image size */
  size?: UserImageSizes;
  /** User image URL */
  src?: string;
}

/**
 * Displays the user image if it's available, otherwise displays the user
 * initials with a randomized background color based on the user name.
 */
export const UserImage = ({
  className = "",
  hasBorder = false,
  isFullRounded = false,
  name,
  size = "6",
  src,
}: UserImageProps) => {
  const hasImage = Boolean(src);
  const [hasError, setHasError] = useState(false);

  const handleOnError = useCallback(() => {
    setHasError(true);
  }, []);

  const initials = useMemo(() => nameToInitials(name), [name]);

  const style = useMemo(
    () => ({ backgroundColor: nameToHslColor(name) }),
    [name],
  );

  const shouldShowImage = hasImage && !hasError;

  return (
    <div
      className={classNames(
        className,
        "relative inline-flex size-fit shrink-0 grow-0 overflow-hidden",
        {
          [borderRadiusSizes[size]]: !hasBorder && !isFullRounded,
          [borderRadiusWithBorderSizes[size]]: hasBorder && !isFullRounded,
          "border-2 border-inherit": hasBorder,
          "rounded-full": isFullRounded,
        },
      )}
      data-testid="user-image-container"
    >
      {shouldShowImage && (
        <img
          alt=""
          className={classNames(
            "absolute left-0 top-0 select-none object-cover",
            boxSizes[size],
          )}
          data-testid="user-image-element"
          src={src}
          onError={handleOnError}
          loading="lazy"
        />
      )}
      <div
        aria-hidden="true"
        className={classNames(
          "flex select-none items-center justify-center rounded-[inherit] font-medium text-white",
          boxSizes[size],
          textSizes[size],
          {
            invisible: shouldShowImage,
          },
        )}
        data-testid="user-image-initials"
        style={style}
      >
        {initials}
      </div>
    </div>
  );
};

UserImage.displayName = "UserImage";
